// LT-341: TypeScript shapes for the /agents REST API. Mirrors the
// Pydantic schemas in backend/schemas.py so the frontend reads the
// JSON shape directly without a transform layer.

import type { CampaignFieldDef } from './campaign';

export interface AgentVersion {
  id: string;
  agent_id: string;
  version_number: number;
  prompt_template: string;
  voice_id_male: string | null;
  voice_id_female: string | null;
  capabilities: string[];
  // LT-406: L1 reusable persona fields. NULL on rows created before
  // LT-406. The new agent edit form (LT-411) writes these directly.
  tone: string | null;
  agent_persona: string | null;
  objection_handling_style: string | null;
  // LT-663: curated suggested input/output data schema. The campaign wizard
  // pre-fills a campaign's input_schema / output_schema from these when this
  // agent is selected. The backend always sends them (defaults to []);
  // optional here so older hand-built test fixtures still type-check.
  suggested_input_schema?: CampaignFieldDef[];
  suggested_output_schema?: CampaignFieldDef[];
  created_at: string;
  created_by_user_id: string | null;
}

export interface Agent {
  id: string;
  workspace_id: string | null;
  is_system: boolean;
  category: string;
  name: string;
  description: string | null;
  current_version: AgentVersion | null;
  created_by_user_id: string | null;
  created_at: string;
  updated_at: string | null;
  deprecated_at: string | null;
}

export interface AgentCreate {
  name: string;
  category: string;
  prompt_template?: string;
  voice_id_male?: string | null;
  voice_id_female?: string | null;
  // LT-554: operators never see raw voice IDs — send a gender + language
  // enum and the backend resolves the ElevenLabs voice_id into the slot
  // matching the gender (services.voice_map). An explicit voice_id_* wins.
  voice_gender?: 'male' | 'female';
  voice_language?: 'en' | 'en_hi' | 'en_id';
  capabilities?: string[];
  description?: string | null;
  // LT-406: L1 persona fields. Optional on initial create; fully
  // editable later via AgentUpdate.
  tone?: string | null;
  agent_persona?: string | null;
  objection_handling_style?: string | null;
}

// Mirrors backend/schemas.py:AgentUpdate. Every field is optional;
// omitted fields inherit from the previous version.
//
// LT-385: metadata-only edits (name and/or description only, with no
// prompt / voice / capabilities / persona) skip the version bump on
// the backend — see services.agents.update_agent short-circuit.
//
// LT-406: ``change_note`` removed — operators don't type version notes.
// Every save bumps the version unconditionally.
export interface AgentUpdate {
  name?: string;
  description?: string | null;
  prompt_template?: string;
  voice_id_male?: string | null;
  voice_id_female?: string | null;
  capabilities?: string[];
  tone?: string | null;
  agent_persona?: string | null;
  objection_handling_style?: string | null;
}

// LT-384: one row in the "Pinned by" section on the Agent detail page.
// Mirrors backend AgentPinnedByStepResponse.
export interface AgentPinnedByStep {
  step_id: string;
  sequence_id: string;
  sequence_name: string;
  position: number;
  step_type: string;
  agent_version_id: string;
  agent_version_number: number;
}

export interface AgentListFilters {
  category?: string;
  includeSystem?: boolean;
  includeDeprecated?: boolean;
}

// Mirrors backend/services/agents.py:ALLOWED_CAPABILITIES. The
// backend is the source of truth — the UI reads any capability key
// that comes back on agent_versions.capabilities and renders it; the
// constant here is for the read-only checklist + clone defaults so
// the catalogue page doesn't need a separate API roundtrip.
export const ALLOWED_CAPABILITIES = [
  'end_call',
  'transfer_to_human',
  'book_meeting',
  'schedule_callback',
  'lookup_info',
  'check_availability',
  'negotiate_payment',
  'prequalify_contact',
  'cold_outreach',
] as const;

export type AllowedCapability = (typeof ALLOWED_CAPABILITIES)[number];

export const REQUIRED_CAPABILITIES: AllowedCapability[] = ['end_call', 'lookup_info'];

// Display labels for the capability checklist. Keep in sync with
// backend ALLOWED_CAPABILITIES; new capability → add a label here.
export const CAPABILITY_LABELS: Record<string, string> = {
  end_call: 'End call',
  transfer_to_human: 'Transfer to human',
  book_meeting: 'Book meeting',
  schedule_callback: 'Schedule callback',
  lookup_info: 'Lookup info',
  check_availability: 'Check availability',
  negotiate_payment: 'Negotiate payment',
  prequalify_contact: 'Pre-qualify contact',
  cold_outreach: 'Cold outreach',
};
